import React from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Link,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { isWarrantOrRight } from "../utils/spacThresholds";
import { exactExposure, exactPercent, exactShortExposure } from "../utils/hoverPrecision";
import HoverValue from "./HoverValue";
import { formatPercent, negativeColor, SMV_NEGATIVE_COLOR } from "../../../utils/formatters";

// Dollar values, same as Firmwide Summary: millions (2 decimals under $10M, 1 under $100M, none above)
// from $100k up, thousands below that; negatives in brackets; zero/missing as "-".
const formatMoney = (value) => {
  if (!value) return "-";
  const abs = Math.abs(value);
  let body;
  if (abs >= 1e5) {
    const millions = abs / 1e6;
    const decimals = millions < 10 ? 2 : millions < 100 ? 1 : 0;
    body = `$${millions.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}M`;
  } else {
    body = `$${Math.round(abs / 1e3).toLocaleString("en-US")}K`;
  }
  return value < 0 ? `(${body})` : body;
};

export const ALL_EXPOSURES = ["LMV", "SMV", "GMV", "NMV"];

// Exposure % and VaR columns: ~10% wider than the auto-sized default (~100px).
const WIDE_COL = { minWidth: 110 };

const pctOfCapital = (value, riskCapital) => (riskCapital ? (Math.abs(value) / riskCapital) * 100 : null);
const exceedsCap = (pct, cap) => Number.isFinite(pct) && Number.isFinite(cap) && cap > 0 && pct > cap;

/**
 * Position Details tab: strategy -> investment code position-level
 * breakdown, sortable, with over-cap rows highlighted. Was previously the
 * drill-down modal's only view; now one of two tabs (the other being
 * StrategyTypeRiskAnalytics).
 */
export default function PositionDetailsTable({
  level,
  selectedStrategy,
  viewMode,
  rows,
  sortField,
  sortDir,
  onSort,
  onStrategyClick,
  onInvestmentTypeClick,
  onBack,
  backLabel = null,
  lmvOnly = false,
  wtRtLimitPct = null,
  wtRtLimitLabel = null,
  investmentTypeCode = null,
  riskCapital,
  limitCaps = {},
  varAsOf = null,
  varUnavailable = false,
  greekScenarios = [],
  visibleExposures = ALL_EXPOSURES,
}) {
  // VaR is per strategy (Risk Monitor doesn't break it out per position here), and per
  // investment / investment type for SPACs, whose rows carry summed VaR.
  const showVar = level !== "investments" || rows.some((r) => "var_95" in r);
  // Greeks columns: only scenarios with a value for at least one visible row (SPAC: Delta only, etc.).
  const visibleGreeks = greekScenarios.filter((name) => rows.some((r) => r.greeks?.[name]));
  // SPAC (lmvOnly) always shows LMV only; otherwise the selected exposures.
  const show = (measure) => (lmvOnly ? measure === "LMV" : visibleExposures.includes(measure));
  const greekField = (name) => `greek:${name}`;
  const formatVar = (value) => (value == null ? "—" : formatMoney(value));
  const sortableHeader = (label, field, sx) => (
    <TableCell align="right" sx={sx}>
      <TableSortLabel active={sortField === field} direction={sortField === field ? sortDir : "asc"} onClick={() => onSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const isIssuerView = viewMode === "issuer" && level === "strategies";

  const getLabelText = () => {
    if (isIssuerView) return "Issuer";
    if (level === "investmentTypes") return "Investment Type";
    if (level === "strategies") return "Strategy";
    return "Investment Code";
  };

  return (
    <>
      {backLabel && (
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
          {backLabel}
        </Button>
      )}
      {showVar && level !== "investments" && (
        <Typography variant="caption" color={varUnavailable ? "error" : "text.secondary"} sx={{ display: "block", mb: 1 }}>
          {varUnavailable
            ? "Strategy VaR unavailable — Risk Monitor could not be reached."
            : varAsOf
              ? `VaR from Risk Monitor simulation as of ${varAsOf}.`
              : "No Risk Monitor VaR for this date."}
        </Typography>
      )}
      {/*{lmvOnly && wtRtLimitPct != null && (*/}
      {/*  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>*/}
      {/*    Warrant and right rows are checked against the {wtRtLimitLabel} ({formatPercent(wtRtLimitPct, 2)} LMV/RC); equity (CS) rows against the max LMV % limit*/}
      {/*    {limitCaps.max_lmv_pct != null ? ` (${formatPercent(limitCaps.max_lmv_pct, 2)})` : ""}. Rows above their threshold are highlighted.*/}
      {/*  </Typography>*/}
      {/*)}*/}
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: "action.hover" }}>
            <TableCell sx={{ fontWeight: 600 }}>{getLabelText()}</TableCell>
            {(level === "strategies" || level === "investmentTypes") && <TableCell align="center">Positions</TableCell>}
            {show("LMV") && sortableHeader("LMV", "lmv")}
            {show("SMV") && sortableHeader("SMV", "smv")}
            {show("GMV") && sortableHeader("GMV", "gross_exposure")}
            {show("NMV") && sortableHeader("NMV", "nmv")}
            {show("LMV") && sortableHeader(lmvOnly ? "LMV/RC %" : "LMV %", "lmv_pct", WIDE_COL)}
            {lmvOnly && <TableCell align="right">Threshold %</TableCell>}
            {show("SMV") && !lmvOnly && sortableHeader("SMV %", "smv_pct", WIDE_COL)}
            {show("GMV") && !lmvOnly && sortableHeader("GMV %", "gmv_pct", WIDE_COL)}
            {show("NMV") && !lmvOnly && sortableHeader("NMV %", "nmv_pct", WIDE_COL)}
            {showVar && sortableHeader("VaR 95%", "var_95", WIDE_COL)}
            {showVar && sortableHeader("VaR 99%", "var_99", WIDE_COL)}
            {visibleGreeks.map((name) => sortableHeader(name, greekField(name)))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={1 + (level === "strategies" || level === "investmentTypes" ? 1 : 0) + ALL_EXPOSURES.filter(show).length * (lmvOnly ? 1 : 2) + (lmvOnly ? 2 : 0) + (showVar ? 2 : 0) + visibleGreeks.length} align="center" sx={{ py: 3 }}>
                <Typography color="text.secondary">No data found.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const lmvPct = pctOfCapital(row.lmv, riskCapital);
              const smvPct = pctOfCapital(row.smv, riskCapital);
              const gmvPct = pctOfCapital(row.gross_exposure, riskCapital);
              const nmv = row.lmv - row.smv;
              const nmvPct = pctOfCapital(nmv, riskCapital);
              // SPAC thresholds by investment type: warrants/rights use the WT/RT limit (or the PM approval
              // level); equity (CS) uses the normal max LMV % from Thresholds Admin; other types have none.
              const rowType = level === "investmentTypes" ? row.investment_type_code : investmentTypeCode;
              let rowThresholdPct = null;
              let rowThresholdLabel = null;
              if (lmvOnly && isWarrantOrRight(rowType)) {
                rowThresholdPct = wtRtLimitPct;
                rowThresholdLabel = wtRtLimitLabel;
              } else if (lmvOnly && rowType === "CS") {
                rowThresholdPct = limitCaps.max_lmv_pct ?? null;
                rowThresholdLabel = "Max LMV % (Thresholds Admin)";
              }
              const lmvOver = exceedsCap(lmvPct, lmvOnly ? rowThresholdPct : limitCaps.max_lmv_pct);
              const smvOver = exceedsCap(smvPct, limitCaps.max_smv_pct);
              const gmvOver = exceedsCap(gmvPct, limitCaps.max_gmv_pct);
              const rowKey = isIssuerView
                ? row.issuer_name
                : level === "investmentTypes"
                  ? row.investment_type_code
                  : level === "strategies"
                    ? row.strategy_name
                    : row.investment_code;
              return (
                <TableRow key={rowKey} sx={{ backgroundColor: (lmvOnly ? lmvOver : lmvOver || smvOver || gmvOver) ? "rgba(244, 67, 54, 0.08)" : "inherit" }}>
                  <TableCell sx={{ fontWeight: (level === "strategies" || level === "investmentTypes") && !isIssuerView ? 600 : 400, minWidth: "240px", maxWidth: "900px", whiteSpace: "normal", wordBreak: "break-word" }}>
                    {level === "investmentTypes" ? (
                      <Link component="button" variant="body2" onClick={() => onInvestmentTypeClick(row.investment_type_code)} sx={{ cursor: "pointer", textAlign: "left" }}>
                        {row.investment_type_label}
                      </Link>
                    ) : isIssuerView ? (
                      <Link component="button" variant="body2" onClick={() => onStrategyClick(row.issuer_name)} sx={{ cursor: "pointer", textAlign: "left" }}>
                        {row.issuer_name || "—"}
                      </Link>
                    ) : level === "strategies" ? (
                      <Link component="button" variant="body2" onClick={() => onStrategyClick(row.strategy_name)} sx={{ cursor: "pointer", textAlign: "left" }}>
                        {row.strategy_name}
                      </Link>
                    ) : (
                      row.investment_code || "—"
                    )}
                  </TableCell>
                  {(level === "strategies" || level === "investmentTypes") && <TableCell align="center">{row.position_count}</TableCell>}
                  {show("LMV") && (
                    <TableCell align="right" sx={{ color: negativeColor(row.lmv) }}>
                      <HoverValue title={exactExposure(row.lmv)}>{formatMoney(row.lmv)}</HoverValue>
                    </TableCell>
                  )}
                  {show("SMV") && (
                    <TableCell align="right" sx={{ color: row.smv ? SMV_NEGATIVE_COLOR : "inherit" }}>
                      <HoverValue title={exactShortExposure(row.smv)}>{formatMoney(-Math.abs(row.smv))}</HoverValue>
                    </TableCell>
                  )}
                  {show("GMV") && (
                    <TableCell align="right" sx={{ color: negativeColor(row.gross_exposure) }}>
                      <HoverValue title={exactExposure(row.gross_exposure)}>{formatMoney(row.gross_exposure)}</HoverValue>
                    </TableCell>
                  )}
                  {show("NMV") && (
                    <TableCell align="right" sx={{ color: negativeColor(nmv) }}>
                      <HoverValue title={exactExposure(nmv)}>{formatMoney(nmv)}</HoverValue>
                    </TableCell>
                  )}
                  {show("LMV") && (
                    <TableCell align="right" sx={{ ...WIDE_COL, fontWeight: 600, color: lmvOver ? "error.main" : "inherit" }}>
                      {lmvPct == null ? "—" : (
                        <HoverValue title={`${exactPercent(lmvPct, lmvOnly ? 2 : 1)} | Max: ${limitCaps.max_lmv_pct != null ? formatPercent(limitCaps.max_lmv_pct, 2) : "—"}`}>{formatPercent(lmvPct, lmvOnly ? 2 : 1)}</HoverValue>
                      )}
                    </TableCell>
                  )}
                  {lmvOnly && (
                    <TableCell align="right" sx={{ color: rowThresholdPct == null ? "text.disabled" : "text.secondary" }}>
                      {rowThresholdPct == null ? "—" : (
                        <HoverValue title={`${rowThresholdLabel}: ${exactPercent(rowThresholdPct, 2)}`}>{formatPercent(rowThresholdPct, 2)}</HoverValue>
                      )}
                    </TableCell>
                  )}
                  {show("SMV") && !lmvOnly && (
                    <TableCell align="right" sx={{ ...WIDE_COL, fontWeight: 600, color: smvOver ? "error.main" : "inherit" }}>
                      {smvPct == null ? "—" : <HoverValue title={`${exactPercent(smvPct, 1)} | Max: ${limitCaps.max_smv_pct != null ? formatPercent(limitCaps.max_smv_pct, 2) : "—"}`}>{formatPercent(smvPct, 1)}</HoverValue>}
                    </TableCell>
                  )}
                  {show("GMV") && !lmvOnly && (
                    <TableCell align="right" sx={{ ...WIDE_COL, fontWeight: 600, color: gmvOver ? "error.main" : "inherit" }}>
                      {gmvPct == null ? "—" : <HoverValue title={`${exactPercent(gmvPct, 1)} | Max: ${limitCaps.max_gmv_pct != null ? formatPercent(limitCaps.max_gmv_pct, 2) : "—"}`}>{formatPercent(gmvPct, 1)}</HoverValue>}
                    </TableCell>
                  )}
                  {show("NMV") && !lmvOnly && (
                    <TableCell align="right" sx={{ ...WIDE_COL, fontWeight: 600 }}>
                      {nmvPct == null ? "—" : <HoverValue title={`${exactPercent(nmvPct, 1)} | Max: ${limitCaps.max_nmv_pct != null ? formatPercent(limitCaps.max_nmv_pct, 2) : "—"}`}>{formatPercent(nmvPct, 1)}</HoverValue>}
                    </TableCell>
                  )}
                  {showVar && (
                    <>
                      <TableCell align="right" sx={{ ...WIDE_COL, color: row.var_95 < 0 ? "error.main" : "inherit" }}>
                        <HoverValue title={exactExposure(row.var_95)}>{formatVar(row.var_95)}</HoverValue>
                      </TableCell>
                      <TableCell align="right" sx={{ ...WIDE_COL, color: row.var_99 < 0 ? "error.main" : "inherit" }}>
                        <HoverValue title={exactExposure(row.var_99)}>{formatVar(row.var_99)}</HoverValue>
                      </TableCell>
                    </>
                  )}
                  {visibleGreeks.map((name) => {
                    const value = row.greeks?.[name];
                    return (
                      <TableCell key={name} align="right" sx={{ color: value < 0 ? "error.main" : "inherit" }}>
                        {value ? <HoverValue title={exactExposure(value)}>{formatMoney(value)}</HoverValue> : "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </>
  );
}
